import { Badge } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMatrixUser } from 'matrix/hooks/useMatrixUser';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypingUsers } from '../../../../matrix/hooks/useTypingUsers';
import styles from './MatrixRoomListItem.module.scss';

type MessageDetailProps = {
  className?: string | undefined;
  lastUpdate: number;
  unread: number;
  roomName: string;
  badgeText?: string;
  preview: string;
  isSelected: boolean;
  onClick: (roomId: string) => void;
  roomId: string;
  otherMatrixUserId?: string;
};

export const MatrixRoomListItem = memo(
  ({
    isSelected,
    lastUpdate,
    className,
    roomName,
    unread,
    onClick,
    roomId,
    badgeText,
    preview,
    otherMatrixUserId,
  }: MessageDetailProps) => {
    const { t } = useTranslation();

    const { isCurrentlyActive } = useMatrixUser(otherMatrixUserId || '');

    const fromNow = dayjs(dayjs(lastUpdate)).fromNow(false);
    const classes = classNames(
      styles.message,
      {
        [styles.unread]: unread > 0,
        [styles.selected]: isSelected,
      },
      className,
    );

    const typingUsers = useTypingUsers(roomId);

    return (
      <div onClick={() => onClick(roomId)} className={classes}>
        <div className={styles.top}>
          <div className={styles.name}>
            {isCurrentlyActive && <div className={styles.online} title={t('messages.online')} />}
            {roomName}
          </div>
          <div className={styles.date}>
            {typingUsers.length ? t('messages.isTyping', { count: typingUsers.length, name: '' }) : fromNow}
          </div>
        </div>
        <div className={styles.middle}>
          {preview !== '' && <div className={styles.preview}>{preview}</div>}
          {unread > 0 && <div className={styles.indicator}>{unread >= 10 ? '9+' : unread}</div>}
        </div>
        <div className={styles.bottom}>
          <div className={styles.room}>{badgeText}</div>
        </div>
      </div>
    );
  },
);

MatrixRoomListItem.displayName = 'Message';
